import { useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Skeleton,
} from '@loader/ui';

import { useNangoIntegrations } from '../hooks/useNangoConnectors';
import type { NangoIntegration } from '../hooks/useNangoConnectors';
import { CONNECTOR_OPTIONS } from '../types';
import type { ConnectorType } from '../types';

interface IntegrationPickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (integration: NangoIntegration) => void;
  onCustomConnectorSelect?: (type: ConnectorType) => void;
  /** Provider config keys that already have a Nango connection */
  connectedProviderKeys?: Set<string>;
  /** Provider names that already have an integration connection (e.g. 'hubspot') */
  connectedIntegrationProviders?: Set<string>;
}

export function IntegrationPickerDialog({
  open,
  onOpenChange,
  onSelect,
  onCustomConnectorSelect,
  connectedProviderKeys,
  connectedIntegrationProviders,
}: IntegrationPickerDialogProps) {
  const { data: integrations, isLoading } = useNangoIntegrations();

  const available = useMemo(
    () => integrations?.filter((i) => !connectedProviderKeys?.has(i.uniqueKey)),
    [integrations, connectedProviderKeys]
  );

  const namedConnectors = useMemo(
    () => CONNECTOR_OPTIONS.filter((o) => {
      if (!o.available || !o.named) return false;
      return !o.providerName || !connectedIntegrationProviders?.has(o.providerName);
    }),
    [connectedIntegrationProviders]
  );
  const genericConnectors = useMemo(
    () => CONNECTOR_OPTIONS.filter((o) => o.available && !o.named && o.type !== 'nango'),
    []
  );

  const handleSelect = (integration: NangoIntegration) => {
    onSelect(integration);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>New Integration</DialogTitle>
          <DialogDescription>
            Choose a data source to connect.
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="grid grid-cols-2 gap-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-16 rounded-lg" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {namedConnectors.map((option) => (
              <button
                key={option.type}
                type="button"
                onClick={() => {
                  onCustomConnectorSelect?.(option.type);
                  onOpenChange(false);
                }}
                className="border-border hover:border-primary/40 hover:bg-accent/50 flex items-center gap-3 rounded-lg border px-3 py-3 text-left transition-colors"
              >
                {option.logo ? (
                  <img src={option.logo} alt="" className="h-6 w-6 shrink-0 rounded-full object-contain" />
                ) : (
                  <option.icon className="text-muted-foreground h-6 w-6 shrink-0" />
                )}
                <span className="text-sm font-medium leading-tight">{option.label}</span>
              </button>
            ))}
            {available?.map((integration) => (
              <button
                key={integration.uniqueKey}
                type="button"
                onClick={() => handleSelect(integration)}
                className="border-border hover:border-primary/40 hover:bg-accent/50 flex items-center gap-3 rounded-lg border px-3 py-3 text-left transition-colors"
              >
                {integration.logo ? (
                  <img
                    src={integration.logo}
                    alt=""
                    className="h-6 w-6 shrink-0 rounded object-contain"
                  />
                ) : (
                  <div className="bg-muted h-6 w-6 shrink-0 rounded" />
                )}
                <span className="text-sm font-medium leading-tight">
                  {integration.displayName}
                </span>
              </button>
            ))}
          </div>
        )}

        {/* Generic connectors section */}
        <div className="border-border mt-2 border-t pt-4">
          <p className="text-muted-foreground mb-3 text-xs font-medium uppercase tracking-wider">Custom</p>
          <div className="grid grid-cols-2 gap-3">
            {genericConnectors.map((option) => (
              <button
                key={option.type}
                type="button"
                onClick={() => {
                  onCustomConnectorSelect?.(option.type);
                  onOpenChange(false);
                }}
                className="border-border hover:border-primary/40 hover:bg-accent/50 flex items-center gap-3 rounded-lg border px-3 py-3 text-left transition-colors"
              >
                <option.icon className="text-muted-foreground h-6 w-6 shrink-0" />
                <div>
                  <span className="text-sm font-medium leading-tight">{option.label}</span>
                  <p className="text-muted-foreground text-xs">{option.description}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
